'use client';

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { Fragment, useState } from 'react';

import { AdvertisementPublicData } from '@cineamo/api/helpers/advertisement-helper';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import AdvertisementCreative from '@cineamo/ui/src/advertisement/AdvertisementCreative';

import { AdvertisementRenderLink, DefaultAdvertisementLink, resolveAdvertisementLinkTarget } from './link';

// darkBlue.DEFAULT from @cineamo/ui/src/tailwind/themes.ts — used when the host has no per-domain
// theme color of its own to pass as `backdropColor`.
const DEFAULT_BACKDROP_COLOR = '#000023';

type LayerAdvertisementProps = {
    onClose: () => void;
    onClick: () => void;
    advertisement: AdvertisementPublicData;
    // Defaults to a plain `<a>` — hosts with locale-aware, typed routing must inject their own.
    renderLink?: AdvertisementRenderLink;
    // Desktop only: pushes the layer down below a host-rendered info banner, when one is visible.
    isInfoBannerVisible?: boolean;
    // Forces the mobile modal layout regardless of viewport width (e.g. detected server-side).
    isMobileUserAgent?: boolean;
    // Backdrop color behind the mobile modal — pass the host's own theme color when it has one.
    backdropColor?: string;
};

const LayerAdvertisement: React.FC<LayerAdvertisementProps> = ({
    onClick,
    onClose,
    advertisement,
    renderLink = DefaultAdvertisementLink,
    isInfoBannerVisible = false,
    isMobileUserAgent = false,
    backdropColor = DEFAULT_BACKDROP_COLOR
}) => {
    const t = useTranslations();
    const isMounted = useIsMounted();
    const [isOpen, setIsOpen] = useState(true);

    const isMobileResolution = useIsMobile();
    const isMobile = isMounted ? isMobileResolution || isMobileUserAgent : false;

    if (!isMounted) {
        return null;
    }

    if (!advertisement.layerImageUrl) {
        return null;
    }

    const target = resolveAdvertisementLinkTarget(advertisement);
    const adName = advertisement.contentName ?? advertisement.advertiserName ?? '';

    const adLabel = t('common.advertisement');

    // Desktop
    if (!isMobile) {
        return (
            <div
                className={cn(
                    'z-30 sticky right-8 drop-shadow-[-4px_8px_16px_rgba(0,0,0,0.15)]',
                    isInfoBannerVisible ? 'top-48' : 'top-8'
                )}>
                <div className="absolute top-8 right-8 w-[240px] h-[300px]">
                    <AdvertisementCreative
                        src={advertisement.layerImageUrl}
                        alt={`${adName} Layer Advertisement`}
                        width={240}
                        height={300}
                        imageClassName="w-[240px] h-[300px]"
                        label={adLabel}
                        closeClassName="absolute top-4 right-4"
                        onClose={onClose}
                        wrapLink={(children) => renderLink({ target, onClick, children })}
                    />
                </div>
            </div>
        );
    }

    // Mobile
    return (
        <Transition
            show={isOpen}
            as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[60]"
                open={isOpen}
                onClose={() => {
                    onClose();
                    setIsOpen(false);
                }}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div
                        className="fixed inset-0 backdrop-blur-md"
                        style={{
                            backgroundColor: withOpacity(backdropColor ?? '', 0.85) ?? undefined
                        }}
                    />
                </TransitionChild>

                <div className="fixed inset-0 flex items-center justify-center">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95">
                        <DialogPanel className="relative m-[24px]">
                            <AdvertisementCreative
                                src={advertisement.layerImageUrl}
                                alt={`${adName} Layer Advertisement`}
                                width={1080}
                                height={1350}
                                imageClassName="w-auto h-auto max-w-[calc(100dvw-(64px+24px))] max-h-[calc(100dvh-(64px+24px))]"
                                label={adLabel}
                                closeClassName="absolute -right-[16px] -top-[16px] p-24"
                                onClose={onClose}
                                wrapLink={(children) => renderLink({ target, onClick, children })}
                            />
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
};

export default LayerAdvertisement;
